'use client'

import { useGyymClient } from '@gyym/sdk-react'
import { Backdrop, LineButton, Link, Loading, useSnackbar } from '@gyym/ui'
import { useRouter, useSearchParams } from 'next/navigation'
import React from 'react'

import {
  type FormValues,
  LoginForm,
  type SubmitContext,
} from '@/components/Forms/LoginForm'
import { Breadcrumbs, Content, Heading, Page } from '@/components/Layouts/Page'
import { TextSeparator } from '@/components/UI/TextSeparator'
import { pushLoginEvent } from '@/lib/googleTagManager'
import { useAuthUser, useIntendedPath, useLineAuthUrl } from '@/modules/Auth'

const DEFAULT_POST_LOGIN_PATH = '/facilities?sortBy=distance&order=asc'

const useSnsParams = () => {
  const searchParams = useSearchParams()

  const userSocialAuthenticationId =
    searchParams?.get('userSocialAuthenticationId') ?? undefined
  const expires = searchParams?.has('expires')
    ? Number(searchParams.get('expires'))
    : undefined
  const signature = searchParams?.get('signature') ?? undefined

  return {
    isSnsLogin:
      typeof userSocialAuthenticationId === 'string' &&
      typeof expires === 'number' &&
      expires > 0 &&
      typeof signature === 'string',
    userSocialAuthenticationId,
    params: {
      signature: signature as string,
      expires: expires as number,
    },
  }
}

const useNotifyQueryErrorEffect = () => {
  const searchParams = useSearchParams()
  const { openSnackbar } = useSnackbar()
  const error = searchParams?.get('error') ?? ''

  React.useEffect(() => {
    if (!error) return

    openSnackbar({
      variant: 'error',
      message: 'エラーが発生しました',
      extra: error,
    })
  }, [error, openSnackbar])
}

const useVerifyEmailIntendedPath = () => {
  const params = useSearchParams()

  const key = params?.get('key')
  const expires = params?.get('expires')
  const signature = params?.get('signature')

  if (key && expires && signature) {
    return `/facilities?key=${key}&expires=${expires}&signature=${signature}`
  }

  return DEFAULT_POST_LOGIN_PATH
}

// Flag to check if SNS direct auth is being requested
let isRequestingDirectAuth = false

export default function LoginPage() {
  const { mutate } = useAuthUser()
  const router = useRouter()
  const client = useGyymClient()
  const verifyEmailIntendedPath = useVerifyEmailIntendedPath()
  const intended = useIntendedPath() ?? verifyEmailIntendedPath
  const lineAuthUrl = useLineAuthUrl(intended)
  const { openSnackbar } = useSnackbar()
  const snsParams = useSnsParams()

  // Is requesting data before navigation (direct auth)
  const [isWaitingNavigation, setIsWaitingNavigation] = React.useState(false)

  const navigateNext = React.useCallback(() => {
    router.push(intended)
  }, [router, intended])

  const onSubmit = React.useCallback(
    async ({ email, password }: FormValues, { setError }: SubmitContext) => {
      try {
        const authUser = await client.auth.user.login(email, password)
        await mutate(authUser, true)
        pushLoginEvent(authUser)
        navigateNext()
      } catch (e) {
        const errors = (e as { errors: Record<string, string[]> }).errors ?? {}
        if (errors.email && errors.email[0]?.match(/試行回数/)) {
          setError('root', {
            type: 'serverError',
            message: errors.email[0],
          })
        } else {
          setError('root', {
            type: 'serverError',
            message: 'メールアドレスもしくはパスワードが正しくありません',
          })
        }
      }
    },
    [client.auth.user, mutate, navigateNext],
  )

  // Login via SNS OAuth callback
  React.useEffect(() => {
    const { isSnsLogin, userSocialAuthenticationId, params } = snsParams

    // Do nothing if not SNS login
    if (!isSnsLogin || !userSocialAuthenticationId) return

    // Run SNS direct auth only once
    if (isRequestingDirectAuth) return
    isRequestingDirectAuth = true

    setIsWaitingNavigation(true)

    const snsLogin = async () => {
      try {
        // Direct Auth
        await client.auth.user.directUserAuthForSocialLogin(
          userSocialAuthenticationId,
          params,
        )

        navigateNext()
        setIsWaitingNavigation(false)
      } catch (e) {
        setIsWaitingNavigation(false)

        if (e instanceof Error) {
          openSnackbar({
            variant: 'error',
            message: 'エラーが発生しました',
            extra: e.message,
          })
        }
      }
    }

    snsLogin()
  }, [snsParams, client, openSnackbar, navigateNext, isWaitingNavigation])

  // Show snackbar if `error` query is present
  useNotifyQueryErrorEffect()

  return (
    <Page>
      <Breadcrumbs
        breadcrumbs={[
          {
            label: 'TOP',
            href: '/',
          },
          {
            label: 'ログイン',
            href: '/login',
          },
        ]}
      />

      <Heading>ログイン</Heading>

      <Content gap={10}>
        <div className="flex w-full items-center justify-center">
          <LineButton
            onClick={() => {
              router.push(lineAuthUrl)
            }}
          >
            LINEでログイン
          </LineButton>
        </div>

        <TextSeparator>または</TextSeparator>

        <LoginForm onSubmit={onSubmit} />

        <div className="flex w-full flex-col justify-center gap-10">
          <div className="flex justify-center">
            <Link href="/help/password_recovery" variant="secondary">
              パスワードを忘れた方はこちら
            </Link>
          </div>
          <div className="flex justify-center">
            <Link href="/signup" variant="primary">
              無料で新規登録する
            </Link>
          </div>
        </div>
      </Content>

      <Backdrop open={isWaitingNavigation} mode="light">
        <Loading size="md" centered />
      </Backdrop>
    </Page>
  )
}
